import {
    updateVendorCapabilities,
    VENDOR_CAPABILITY_LABEL,
    type VendorCapability,
} from '@api/vendors';
import useToaster from '@hooks/toaster/useToaster';
import { MultiSelect } from '@mantine/core';
import { type FC, useEffect, useState } from 'react';

const ALL: VendorCapability[] = ['FG_SUPPLIER', 'RM_SUPPLIER', 'MANUFACTURER', 'FREIGHT_FORWARDER'];

/**
 * What a vendor does for the brand, edited in place. Tagging decides where a vendor is offered:
 * suppliers on purchase orders, manufacturers on production orders, forwarders on a shipment's
 * dispatch booking. Saved on close of the dropdown, not per tick.
 */
export const VendorCapabilitiesField: FC<{
    vendorId: string;
    capabilities: VendorCapability[] | null | undefined;
    onSaved: (capabilities: VendorCapability[]) => void;
}> = ({ vendorId, capabilities, onSaved }) => {
    const { showToastSuccess, showToastError } = useToaster();
    const [draft, setDraft] = useState<VendorCapability[]>(capabilities ?? []);
    const [saving, setSaving] = useState(false);
    useEffect(() => setDraft(capabilities ?? []), [capabilities, vendorId]);

    const commit = async () => {
        const before = [...(capabilities ?? [])].sort().join(',');
        if ([...draft].sort().join(',') === before) return;
        setSaving(true);
        try {
            await updateVendorCapabilities(vendorId, draft);
            showToastSuccess({ title: 'Capabilities saved' });
            onSaved(draft);
        } catch (e: any) {
            showToastError({ title: 'Could not save capabilities', subtitle: e?.error?.message || e?.message || 'Please retry.' });
            setDraft(capabilities ?? []);
        } finally {
            setSaving(false);
        }
    };

    return (
        <MultiSelect
            size="xs"
            data={ALL.map((c) => ({ value: c, label: VENDOR_CAPABILITY_LABEL[c] }))}
            value={draft}
            onChange={(v) => setDraft(v as VendorCapability[])}
            onDropdownClose={() => void commit()}
            placeholder="Untagged — offered everywhere"
            disabled={saving}
            clearable
        />
    );
};
